import type { Cart, ProductVariant } from "@commercetools/platform-sdk";
import { useState } from "react";
import { useFreeShipping } from "../hooks/shipping/use-free-shipping";
import { useCartService } from "../hooks/use-cart-service";
import { useStoryblokProductVariants } from "../hooks/use-storyblok-product-variant";
import { formatMoney, formatSubtotalPrice } from "../utils/cart/currency";
import { getVariantLabelFromAttributes } from "../utils/storyblok/product-variant";

const variantAttribute = (
	variant: ProductVariant,
	attribute: string,
	language: string,
): string =>
	variant.attributes?.find((attr) => attr.name === attribute)?.value?.label[
		language
	] as string;

const getSubTotal = (cart?: Cart) => {
	if (!cart) return "";

	return formatSubtotalPrice(cart);
};

export const useCartProducts = () => {
	const { updateCartLineItemQuantity, removeLineItemFromCart, cart } =
		useCartService();
	const [pending, setPending] = useState(false);
	const sbProducts = useStoryblokProductVariants(
		cart?.lineItems.map((item) => `${item.productId}_${item.variant.id}`) ?? [],
	);

	const language = process.env.GATSBY_LANGUAGE;
	if (!language) throw new Error("Language not set");

	const products =
		cart?.lineItems.map((item) => {
			const sbProduct = sbProducts[`${item.productId}_${item.variant.id}`];

			const productImage =
				item.productKey === "gift-box" || item.productKey === "gift-message"
					? {
							src: item.variant.images?.[0]?.url ?? "",
							alt: item.variant.images?.[0]?.label ?? "",
						}
					: {
							src: sbProduct?.images?.[0]?.filename ?? "",
							alt: sbProduct?.images?.[0]?.alt ?? "",
						};

			return {
				id: item.id,
				productName: item.name[language] ?? "",
				productLink: sbProduct?.path,
				productImage: productImage,
				material: variantAttribute(item.variant, "fabric", language),
				color: variantAttribute(item.variant, "color", language),
				price: formatMoney(item.totalPrice),
				quantity: item.quantity,
				lineItemMode: item.lineItemMode,
				variantLabel: getVariantLabelFromAttributes(
					item.variant.attributes,
					true,
				),
				enableQuantityCounter:
					item.lineItemMode !== "GiftLineItem" &&
					item.productKey !== "gift-box" &&
					item.productKey !== "gift-message",
				enableCloseButton: item.lineItemMode !== "GiftLineItem",
				productKey: item.productKey,
			};
		}) ?? [];

	const setQuantity = async (quantity: number, lineItemId: string) => {
		setPending(true);
		try {
			await updateCartLineItemQuantity(lineItemId, quantity);
		} catch (error) {
			// TODO: Add error alert for customer
			console.error(error);
		}

		setPending(false);
	};

	const incrementProductQuantity = async (id: string) => {
		const q = products.find((p) => p.id === id)?.quantity ?? 0;
		await setQuantity(q + 1, id);
	};

	const decrementProductQuantity = async (id: string) => {
		const q = products.find((p) => p.id === id)?.quantity ?? 0;
		await setQuantity(Math.max(q - 1, 1), id);
	};

	const removeFromCart = async (id: string) => {
		setPending(true);
		if (cart) {
			try {
				await removeLineItemFromCart(id);
			} catch (error) {
				// TODO: Add error alert for customer
				console.error(error);
			}
		}
		setPending(false);
	};

	const freeShipping = useFreeShipping(cart);

	return {
		itemsInCart: products.reduce((t, p) => t + p.quantity, 0),
		products,
		removeFromCart,
		decrementProductQuantity,
		incrementProductQuantity,
		freeShipping,
		pending,
		subTotal: getSubTotal(cart),
	};
};
